import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  MenuItem,
  Menu
} from '@mui/material';
import { 
  Add, 
  Edit, 
  Delete, 
  Work,
  School,
  VolunteerActivism,
  SportsEsports,
  Business,
  Description,
  Download
} from '@mui/icons-material';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import API_BASE_URL from '../config';

async function fetchWithFallback(endpoint, options = {}) {
  // Add authorization header if token exists
  const token = localStorage.getItem('access_token');
  if (token) {
    options.headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`
    };
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, options);
  return response;
}

const ResumeBuilder = ({ activities = [] }) => {
  const [experiences, setExperiences] = useState([]);
  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Always use 'academic' resume type
  const resumeType = 'academic';
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  const resumeRef = useRef(null);

  // Update: Automatically generate resume when activities or resume type change
  useEffect(() => {
    if (activities.length > 0) {
      (async () => {
        setLoading(true);
        setError('');
        try {
          const response = await fetchWithFallback(`/generate-resume?resume_type=${resumeType}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ activities })
          });
          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || 'Failed to generate resume');
          }
          const data = await response.json();
          setResume(data.resume);
        } catch (err) {
          setError(err.message);
          setResume(null);
        } finally {
          setLoading(false);
        }
      })();
    } else {
      setResume(null);
      setLoading(false);
      setError('');
    }
  }, [activities, resumeType]);

  const handleDownloadPDF = async () => {
    if (!resumeRef.current) return;
    
    try {
      
      const canvas = await html2canvas(resumeRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`resume-${resumeType}-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  const handleDownloadText = () => {
    if (!resume) return;
    
    try {
      let textContent = `RESUME (${resumeType.toUpperCase()})\n`;
      textContent += '='.repeat(50) + '\n\n';
      
      if (resume.summary) {
        textContent += `SUMMARY\n${'-'.repeat(20)}\n${resume.summary}\n\n`;
      }
      
      if (activities.length > 0) {
        textContent += `EXPERIENCE\n${'-'.repeat(20)}\n`;
        activities.forEach((activity, index) => {
          textContent += `${activity.name || activity.title}\n`;
          const activityExperiences = experiences.filter(e => e.activityTitle === activity.title);
          if (activityExperiences.length > 0) {
            activityExperiences.forEach(exp => {
              textContent += `  • ${exp.title}`;
              if (exp.link) textContent += ` (${exp.link})`;
              textContent += '\n';
            });
          }
          if (index !== activities.length - 1) textContent += '\n';
        });
        textContent += '\n';
      }
      
      if (resume.skills && Array.isArray(resume.skills) && resume.skills.length > 0) {
        textContent += `SKILLS\n${'-'.repeat(20)}\n${resume.skills.join(', ')}\n\n`;
      }
      
      if (resume.education && Array.isArray(resume.education) && resume.education.length > 0) {
        textContent += `EDUCATION\n${'-'.repeat(20)}\n`;
        resume.education.forEach(edu => {
          textContent += `${edu.degree}\n${edu.institution} • ${edu.date}\n\n`;
        });
      }
      
      const blob = new Blob([textContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `resume-${resumeType}-${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error generating text file:', error);
    }
  };

  const handleDownloadMenuOpen = (event) => {
    setDownloadMenuAnchor(event.currentTarget);
  };

  const handleDownloadMenuClose = () => {
    setDownloadMenuAnchor(null);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'linear-gradient(135deg, #7b2ff2 0%, #f357a8 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}>
      <Paper elevation={8} sx={{ borderRadius: 6, maxWidth: 1100, width: '100%', height: 600, maxHeight: '80vh', ml: { xs: -2, md: -8 }, pl: { xs: 2, md: 6 }, p: { xs: 5, md: 8 }, bgcolor: 'white', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px #7b2ff255' }}>
        <Typography variant="h4" fontWeight={900} sx={{ mb: 3, color: '#7b2ff2', letterSpacing: -1, textAlign: 'center' }}>
          📄 Your Academic Resume
        </Typography>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Typography>Generating resume...</Typography>
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error?.toString?.() || String(error)}
          </Alert>
        )}
        {!resume && !loading && activities.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Complete activities in your plan to build your resume!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Mark activities as completed in your dashboard and your resume will be generated automatically.
            </Typography>
          </Box>
        )}
        {resume && (
          <Box ref={resumeRef} sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 3, bgcolor: 'white', width: '100%', maxWidth: 900, height: '100%', overflowY: 'auto', mb: 4 }}>
            {/* Summary */}
            {resume.summary && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Summary
                </Typography>
                <Typography variant="body1">
                  {resume.summary}
                </Typography>
              </Box>
            )}
            {/* Experience - Show user activities */}
            {activities.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Experience
                </Typography>
                {activities.map((activity, index) => (
                  <Box key={index} sx={{ mb: 2, pb: 2, borderBottom: index !== activities.length - 1 ? '1px solid #eee' : 'none' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      {activity.name || activity.title}
                    </Typography>
                    {activity.description && (
                      <Typography variant="body2" color="text.secondary">
                        {activity.description}
                      </Typography>
                    )}
                    {activity.time_commitment && (
                      <Typography variant="body2" color="text.secondary">
                        Time Commitment: {activity.time_commitment}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            )}
            {/* Skills */}
            {resume.skills && Array.isArray(resume.skills) && resume.skills.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Skills
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {resume.skills.map((skill, index) => (
                    <Chip key={index} label={skill} size="small" />
                  ))}
                </Box>
              </Box>
            )}
            {/* Education */}
            {resume.education && Array.isArray(resume.education) && resume.education.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Education
                </Typography>
                {resume.education.map((edu, index) => (
                  <Box key={index} sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      {edu.degree}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {edu.institution} • {edu.date}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
        {resume && (
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleDownloadPDF}
            sx={{
              background: 'linear-gradient(90deg, #ff6b6b 0%, #ffe082 100%)',
              color: '#fff',
              fontWeight: 700,
              borderRadius: 3,
              px: 5,
              py: 1.5,
              textTransform: 'none',
              fontSize: 18,
              boxShadow: '0 2px 8px #ffe08255',
              '&:hover': { background: 'linear-gradient(90deg, #ffe082 0%, #ff6b6b 100%)', color: '#fff' }
            }}
          >
            Download PDF
          </Button>
        )}
      </Paper>
    </Box>
  );
};

export default ResumeBuilder; 